/**
 * 🤖 AUTO-GENERATED FILE - DO NOT EDIT!
 *
 * Source: modules/audio-analysis/schema.py
 * Generate: cd modules/audio-analysis && python -m generate_zod
 *
 * Echo Nest = Spotify Get Audio Analysis (Jehan / Analyzer).
 * Custom    = additive fields this service adds (range + tempo ticks).
 */

import { z } from 'zod';

/** [Echo Nest] Shared shape for bars, beats, and tatums. */
export const echoNestTimeIntervalSchema = z.object({
  /** [Echo Nest] Interval start in seconds from the beginning of the track. */
  start: z.number(),
  /** [Echo Nest] Interval length in seconds. */
  duration: z.number(),
  /** [Echo Nest] Analyzer confidence in [0, 1]. */
  confidence: z.number(),
});
export type EchoNestTimeInterval = z.infer<typeof echoNestTimeIntervalSchema>;

/** [Echo Nest] Analyzer run metadata (version, platform, timing). */
export const echoNestMetaSchema = z.object({
  /** [Echo Nest] Analyzer version string. */
  analyzer_version: z.string(),
  /** [Echo Nest] Host OS of the analyzer. */
  platform: z.string(),
  /** [Echo Nest] Human status, usually OK. */
  detailed_status: z.string(),
  /** [Echo Nest] 0 on success. */
  status_code: z.number().int(),
  /** [Echo Nest] Unix time the analysis finished. */
  timestamp: z.number().int(),
  /** [Echo Nest] Wall seconds spent in analysis. */
  analysis_time: z.number(),
  /** [Echo Nest] How the file was decoded / resampled. */
  input_process: z.string(),
});
export type EchoNestMeta = z.infer<typeof echoNestMetaSchema>;

/** [Echo Nest] Global track features. ``tempo`` is a single BPM in Echo Nest. */
export const echoNestTrackSchema = z.object({
  /** [Echo Nest] Decoded PCM sample count. */
  num_samples: z.number().int(),
  /** [Echo Nest] Track length in seconds. */
  duration: z.number(),
  /** [Echo Nest] Digest of the uploaded file. */
  sample_md5: z.string(),
  /** [Echo Nest] Analysis window offset; 0 here. */
  offset_seconds: z.number(),
  /** [Echo Nest] Analysis window; 0 means the whole file. */
  window_seconds: z.number(),
  /** [Echo Nest] Sample rate used for analysis. */
  analysis_sample_rate: z.number().int(),
  /** [Echo Nest] Channel count used for analysis. */
  analysis_channels: z.number().int(),
  /** [Echo Nest] Estimated fade-in end (seconds). */
  end_of_fade_in: z.number(),
  /** [Echo Nest] Estimated fade-out start (seconds). */
  start_of_fade_out: z.number(),
  /** [Echo Nest] Overall loudness (we use mean RMS, not dB LUFS). */
  loudness: z.number(),
  /** [Echo Nest] Global BPM. Echo Nest: one number. We still fill this. */
  tempo: z.number(),
  /** [Echo Nest] Confidence of the global tempo. */
  tempo_confidence: z.number(),
  /** [Echo Nest] Beats per bar: 3, 4, or 2. */
  time_signature: z.number().int(),
  /** [Echo Nest] Meter confidence. */
  time_signature_confidence: z.number(),
  /** [Echo Nest] Pitch class 0–11 (C=0). */
  key: z.number().int(),
  /** [Echo Nest] Key confidence. */
  key_confidence: z.number(),
  /** [Echo Nest] 0 minor, 1 major. */
  mode: z.number().int(),
  /** [Echo Nest] Mode confidence. */
  mode_confidence: z.number(),
  /** [Echo Nest] Echo Nest codebook stub (we store a digest slice). */
  codestring: z.string(),
  /** [Echo Nest] Codebook version placeholder. */
  code_version: z.number(),
  /** [Echo Nest] EchoPrint stub. */
  echoprintstring: z.string(),
  /** [Echo Nest] EchoPrint version placeholder. */
  echoprint_version: z.number(),
  /** [Echo Nest] Syncstring stub. */
  synchstring: z.string(),
  /** [Echo Nest] Syncstring version placeholder. */
  synch_version: z.number(),
  /** [Echo Nest] Rhythmstring stub. */
  rhythmstring: z.string(),
  /** [Echo Nest] Rhythmstring version placeholder. */
  rhythm_version: z.number(),
});
export type EchoNestTrack = z.infer<typeof echoNestTrackSchema>;

/** [Echo Nest] Verse/chorus-scale region with its own key / tempo / meter. */
export const echoNestSectionSchema = echoNestTimeIntervalSchema.extend({
  /** [Echo Nest] Mean loudness of the section. */
  loudness: z.number(),
  /** [Echo Nest] Section tempo. Echo Nest: often copied from track. */
  tempo: z.number(),
  /** [Echo Nest] Section tempo confidence. */
  tempo_confidence: z.number(),
  /** [Echo Nest] Section key 0–11. */
  key: z.number().int(),
  /** [Echo Nest] Section key confidence. */
  key_confidence: z.number(),
  /** [Echo Nest] 0 minor, 1 major. */
  mode: z.number().int(),
  /** [Echo Nest] Section mode confidence. */
  mode_confidence: z.number(),
  /** [Echo Nest] Section meter. */
  time_signature: z.number().int(),
  /** [Echo Nest] Section meter confidence. */
  time_signature_confidence: z.number(),
});
export type EchoNestSection = z.infer<typeof echoNestSectionSchema>;

/** [Echo Nest] Timbre/pitch atom. Lowest audio grain, independent of the beat grid. */
export const echoNestSegmentSchema = z.object({
  /** [Echo Nest] Segment start (seconds). */
  start: z.number(),
  /** [Echo Nest] Segment length (seconds). */
  duration: z.number(),
  /** [Echo Nest] Segment confidence. */
  confidence: z.number(),
  /** [Echo Nest] Loudness at segment start. */
  loudness_start: z.number(),
  /** [Echo Nest] Peak loudness in the segment. */
  loudness_max: z.number(),
  /** [Echo Nest] Offset of the peak within the segment. */
  loudness_max_time: z.number(),
  /** [Echo Nest] Loudness at segment end. */
  loudness_end: z.number(),
  /** [Echo Nest] 12 chroma bins, C through B. */
  pitches: z.array(z.number()),
  /** [Echo Nest] Echo Nest: 12 basis coefficients. We emit 13 MFCCs (librosa default). */
  timbre: z.array(z.number()),
});
export type EchoNestSegment = z.infer<typeof echoNestSegmentSchema>;

/** [Echo Nest] Document Spotify documented at Get Audio Analysis. */
export const echoNestAudioAnalysisSchema = z.object({
  /** [Echo Nest] meta */
  meta: echoNestMetaSchema,
  /** [Echo Nest] track */
  track: echoNestTrackSchema,
  /** [Echo Nest] bars */
  bars: z.array(echoNestTimeIntervalSchema),
  /** [Echo Nest] beats */
  beats: z.array(echoNestTimeIntervalSchema),
  /** [Echo Nest] tatums */
  tatums: z.array(echoNestTimeIntervalSchema),
  /** [Echo Nest] sections */
  sections: z.array(echoNestSectionSchema),
  /** [Echo Nest] segments */
  segments: z.array(echoNestSegmentSchema),
});
export type EchoNestAudioAnalysis = z.infer<typeof echoNestAudioAnalysisSchema>;

/** [Custom] One BPM sample. Echo Nest never shipped this; tatums only have timing. */
export const tempoTickSchema = echoNestTimeIntervalSchema.extend({
  /** [Custom] Instantaneous BPM for this tick. */
  tempo: z.number(),
  /** [Custom] dtempo = librosa dynamic tempo frame; beat_ioi = 60 / beat duration. */
  source: z.enum(["dtempo", "beat_ioi"]),
});
export type TempoTick = z.infer<typeof tempoTickSchema>;

/** [Custom] Global tempo plus a range when the pulse is bimodal or drifting. */
export const trackSchema = echoNestTrackSchema.extend({
  /** [Custom] Lower BPM of the body (p10 / slow 3:2 cluster / tempogram peak). */
  tempo_min: z.number(),
  /** [Custom] Upper BPM of the body (p90 / fast 3:2 cluster / tempogram peak). */
  tempo_max: z.number(),
});
export type Track = z.infer<typeof trackSchema>;

/** [Custom] Echo Nest meta plus whether this JSON came from the disk cache. */
export const metaSchema = echoNestMetaSchema.extend({
  /** [Custom] True when this JSON was served from disk cache. */
  cached: z.boolean(),
});
export type Meta = z.infer<typeof metaSchema>;

/** [Custom] Echo Nest document plus range + tempo tick series. */
export const audioAnalysisSchema = echoNestAudioAnalysisSchema.extend({
  /** [Custom] meta */
  meta: metaSchema,
  /** [Custom] track */
  track: trackSchema,
  /** [Custom] tempo_ticks */
  tempo_ticks: z.array(tempoTickSchema),
});
export type AudioAnalysis = z.infer<typeof audioAnalysisSchema>;

export const echoNestDocumentKeys = [
  'meta', 'track', 'bars', 'beats', 'tatums', 'sections', 'segments',
] as const;

export const customDocumentKeys = ['tempo_ticks'] as const;
